import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const LearningHub = () => {
  const { user } = useAuth();
  const [resources, setResources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('scout');
  const [scoutContent, setScoutContent] = useState([]);
  const [scoutLoading, setScoutLoading] = useState(false);

  useEffect(() => {
    fetchResources();
  }, [user?.vertical]);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const vertical = user?.vertical || 'WMS';
      const response = await api.get(`/learning/resources?vertical=${encodeURIComponent(vertical)}`);
      const data = response?.data || response;
      setResources(data.resources);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      setResources({ youtube: [], job_boards: [], courses: [], communities: [] });
    } finally {
      setLoading(false);
    }
  };

  const fetchScoutContent = async (tab) => {
    setScoutLoading(true);
    try {
      const domain = user?.vertical?.toLowerCase() || 'wms';

      // Map LearningHub tab keys to wiki category
      const categoryMap = {
        scout: null,
        youtube: 'video',
        job_boards: 'job',
        courses: 'course',
        communities: 'community'
      };

      const category = categoryMap[tab];
      const url = category
        ? `/wiki/feed/learning_hub?domain=${domain}&category=${tab}&limit=20`
        : `/wiki/feed/learning_hub?domain=${domain}&limit=20`;

      const res = await api.get(url);
      const entries = res.data?.entries || res.entries || [];
      setScoutContent(entries);
    } catch (e) {
      setScoutContent([]);
    } finally {
      setScoutLoading(false);
    }
  };

  useEffect(() => {
    fetchScoutContent(activeTab);
  }, [activeTab]);

  const tabs = [
    { key: 'scout', label: '🔍 Scout Finds' },
    { key: 'youtube', label: '📺 Video Channels' },
    { key: 'job_boards', label: '💼 Job Boards' },
    { key: 'courses', label: '🎓 Courses' },
    { key: 'communities', label: '👥 Communities' }
  ];

  if (loading) {
    return <LoadingSpinner message="Loading resources..." />;
  }

  const currentResources = resources?.[activeTab] || [];

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <span style={{ fontSize: '48px' }}>📚</span>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px' }}>Learning Hub</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b' }}>Curated resources for your {user?.vertical || 'professional'} journey</p>
        </div>
      </div>

      {/* Stats Row */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {[
          { icon: '📑', value: 0, label: 'Saved' },
          { icon: '📈', value: 0, label: 'In Progress' },
          { icon: '✅', value: 0, label: 'Completed' },
          { icon: '🏆', value: 0, label: 'Certificates' },
          { icon: '⏱', value: 0, label: 'Hours' }
        ].map((stat, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flex: 1, minWidth: '120px' }}>
            <span style={{ fontSize: '24px' }}>{stat.icon}</span>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 600 }}>{stat.value}</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              background: activeTab === tab.key ? 'linear-gradient(135deg, #0ea5e9, #06b6d4)' : '#f1f5f9',
              color: activeTab === tab.key ? 'white' : '#64748b'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Scout Finds Panel */}
      {activeTab === 'scout' && (
        <div>
          <p style={{color:'#64748b',fontSize:'14px',marginBottom:'16px'}}>
            Fresh content discovered daily by Scout Agent for your {user?.vertical || 'WMS'} domain
          </p>
          {scoutLoading ? (
            <LoadingSpinner message="Loading scout content..." />
          ) : scoutContent.length === 0 ? (
            <div style={{textAlign:'center',padding:'60px',color:'#94a3b8'}}>
              <div style={{fontSize:'48px',marginBottom:'16px'}}>🔍</div>
              <h3>Scout Agent is warming up</h3>
              <p>Fresh domain content appears here daily at 6 AM IST</p>
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:'16px'}}>
              {scoutContent.map(item=>(
                <div key={item.id}
                  style={{background:'white',border:'1px solid #e2e8f0',borderRadius:'12px',padding:'16px',boxShadow:'0 1px 3px rgba(0,0,0,0.05)'}}>
                  <h3 style={{margin:'0 0 8px',fontSize:'15px',lineHeight:'1.4'}}>{item.topic}</h3>
                  <p style={{margin:'0 0 12px',color:'#64748b',fontSize:'13px',lineHeight:'1.5'}}>
                    {(item.summary||item.content||'').substring(0,150)}...
                  </p>
                  {item.source_metadata?.url && (
                    <a href={item.source_metadata.url}
                      target="_blank" rel="noopener noreferrer"
                      style={{display:'inline-block',padding:'6px 14px',background:'#7c3aed',color:'white',borderRadius:'8px',textDecoration:'none',fontSize:'13px'}}>
                      View Source →
                    </a>
                  )}
                  <div style={{marginTop:'10px',display:'flex',gap:'4px',flexWrap:'wrap'}}>
                    {(item.tags||[]).slice(0,4).map((tag,i)=>(
                      <span key={i} style={{padding:'2px 6px',borderRadius:'10px',background:'#f1f5f9',color:'#64748b',fontSize:'11px'}}>{tag}</span>
                    ))}
                  </div>
                  <div style={{marginTop:'8px',fontSize:'11px',color:'#94a3b8'}}>
                    🔍 Scout · {new Date(item.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Resources Grid — wiki feed first, static fallback */}
      {activeTab !== 'scout' && (
        <div>
          {scoutLoading ? (
            <LoadingSpinner message="Loading content..." />
          ) : scoutContent.length > 0 ? (
            <div>
              <h2 style={{fontSize:'18px', marginBottom:'16px'}}>
                {tabs.find(t => t.key === activeTab)?.label} for {user?.vertical}
              </h2>
              <div style={{display:'grid',
                gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',
                gap:'16px'}}>
                {scoutContent.map(item => (
                  <div key={item.id}
                    style={{background:'white',
                      border:'1px solid #e2e8f0',
                      borderRadius:'12px',padding:'16px',
                      boxShadow:'0 1px 3px rgba(0,0,0,0.05)'}}>
                    <h3 style={{margin:'0 0 8px',fontSize:'15px',
                      lineHeight:'1.4'}}>{item.topic}</h3>
                    <p style={{margin:'0 0 12px',color:'#64748b',
                      fontSize:'13px',lineHeight:'1.5'}}>
                      {(item.summary||'').substring(0,150)}...
                    </p>
                    {item.source_metadata?.source_url && (
                      <a href={item.source_metadata.source_url}
                        target="_blank" rel="noopener noreferrer"
                        style={{display:'inline-block',
                          padding:'6px 14px',background:'#7c3aed',
                          color:'white',borderRadius:'8px',
                          textDecoration:'none',fontSize:'13px'}}>
                        View Source →
                      </a>
                    )}
                    <div style={{marginTop:'10px',display:'flex',
                      gap:'4px',flexWrap:'wrap'}}>
                      {(item.tags||[]).slice(0,4).map((tag,i)=>(
                        <span key={i} style={{padding:'2px 6px',
                          borderRadius:'10px',background:'#f1f5f9',
                          color:'#64748b',fontSize:'11px'}}>
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div style={{marginTop:'8px',fontSize:'11px',
                      color:'#94a3b8'}}>
                      🔍 Scout · {new Date(item.created_at)
                        .toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Static fallback when no wiki content */
            <div>
              <h2 style={{ fontSize: '18px', marginBottom: '16px' }}>
                {tabs.find(t => t.key === activeTab)?.label} for {user?.vertical || 'Your Domain'}
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '16px' }}>
                {currentResources.map((resource, index) => (
                  <div key={index}
                    style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '32px' }}>📺</span>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '15px' }}>{resource.name}</h3>
                        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>
                          {resource.description}
                        </p>
                      </div>
                    </div>
                    {resource.url && (
                      <a href={resource.url} target="_blank" rel="noopener noreferrer"
                        style={{ fontSize: '13px', color: '#7c3aed', textDecoration: 'none' }}>
                        Visit ↗
                      </a>
                    )}
                  </div>
                ))}
              </div>
              <p style={{marginTop:'16px', fontSize:'13px',
                color:'#94a3b8', textAlign:'center'}}>
                Scout Agent will populate this section daily with fresh content
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LearningHub;
